import { GenericAbortSignal } from 'axios';

import Upload from 'hooks/useDirectUpload/Upload';
import Workflow, { WorkflowOptions } from 'models/Workflow';
import http from 'services/http';

// A map of declared value/output names -> value. The KEYS are snake_case domain
// identifiers (`patient_first_name`, `clinical_determination`, `final_outcome`) and
// must be sent verbatim — they are not API field names, so OliveBranch leaves them alone.
export type WorkflowValuesInput = Record<string, string>;

// The uploaded packet. The API mints Attachments from these signed_ids and sets the
// `attachments` value, so the FE sends the documents, never the value directly.
// Returns undefined (not []) when empty so the key is OMITTED from the body — sending []
// on an update would wipe the existing packet.
function serializeAttachments(attachments?: Upload[]) {
  const ready = (attachments ?? []).filter((upload) => upload.signedId).map((upload) => upload.serialize());
  return ready.length > 0 ? ready : undefined;
}

export const workflowQueryKeys = {
  base: ['workflows'] as const,
  list: ['workflows', 'list'] as const,
  show: (id: string) => ['workflows', 'show', id] as const,
};

// The inbox: workflows the user can see (allowed_groups scope), recent first.
// A lighter per-row shape than show, but the Workflow model tolerates the missing
// definition and the lighter actions/values.
export async function indexWorkflows(signal?: GenericAbortSignal) {
  return http
    .get<{ data: WorkflowOptions[] }>(`workflows`, { signal })
    .then((res) => res.data.data.map((w) => new Workflow(w)));
}

export type CreateWorkflowInput = {
  // Top-level field keys are camelCase; OliveBranch snake_cases them on the API.
  workflowDefinitionApiName: string;
  // One or more buildings; must be in the user's allowed_groups.
  groupIds: string[];
  // Optional seed of demographics, keyed by declared value name (snake_case).
  values?: WorkflowValuesInput;
  // Optional referral packet documents.
  attachments?: Upload[];
};

export async function createWorkflow({ attachments, ...params }: CreateWorkflowInput) {
  const body = { ...params, attachments: serializeAttachments(attachments) };
  return http.post<WorkflowOptions>(`workflows`, body).then((res) => new Workflow(res.data));
}

export async function showWorkflow(id: string, signal?: GenericAbortSignal) {
  return http.get<WorkflowOptions>(`workflows/${id}`, { signal }).then((res) => new Workflow(res.data));
}

// Editing a referral updates the workflow itself — buildings, demographics, and the
// packet. Only the keys provided are applied (the API rejects an empty update).
export type UpdateWorkflowInput = {
  workflowId: string;
  groupIds?: string[];
  values?: WorkflowValuesInput;
  attachments?: Upload[];
  // The consent cascade (design §9): the action ids the user approved restarting when
  // saving this correction. The API resets each one (instance + its produced values) in
  // the same write; a stale id rejects the whole save with a restart_action_ids error.
  restartActionIds?: string[];
};

export async function updateWorkflow({
  workflowId,
  groupIds,
  values,
  attachments,
  restartActionIds,
}: UpdateWorkflowInput) {
  const body = {
    groupIds,
    values,
    attachments: serializeAttachments(attachments),
    // Omit when empty — an empty array is not a consent list.
    restartActionIds: restartActionIds && restartActionIds.length > 0 ? restartActionIds : undefined,
  };
  return http.put<WorkflowOptions>(`workflows/${workflowId}`, body).then((res) => new Workflow(res.data));
}

export type CompleteWorkflowActionInput = {
  workflowId: string;
  actionId: string;
  // Keyed by declared output name (snake_case), e.g. { clinical_determination: 'meets_criteria' }.
  outputs: WorkflowValuesInput;
};

export async function completeWorkflowAction({ workflowId, actionId, outputs }: CompleteWorkflowActionInput) {
  // `complete` is sent explicitly — the API never assumes it, so a missing intent can't
  // silently finalize an action (it 422s).
  return http
    .patch<WorkflowOptions>(`workflows/${workflowId}/actions/${actionId}`, { invokeAction: 'complete', outputs })
    .then((res) => new Workflow(res.data));
}

// Every action update carries an explicit intent. `complete` (above) sends `outputs`;
// these reuse the same endpoint without an `outputs` body. (`save_progress`, which records
// the action outputs without completing, exists on the API but has no FE entry point yet —
// OLIO-2578.)
export type WorkflowActionIntent = 'rerun' | 'dismiss';

export type InvokeWorkflowActionInput = {
  workflowId: string;
  actionId: string;
  invokeAction: WorkflowActionIntent;
};

export async function invokeWorkflowAction({ workflowId, actionId, invokeAction }: InvokeWorkflowActionInput) {
  return http
    .patch<WorkflowOptions>(`workflows/${workflowId}/actions/${actionId}`, { invokeAction })
    .then((res) => new Workflow(res.data));
}
